import { paths as CustomerCommunicationPaths } from '@luxuryescapes/contract-customer-communication';

import { getDayJs } from '~/services/TimeService';
import { json_headers, request } from '~/services/common';

import { makeCustomerCommunicationV1URL } from './customerCommunicationRequestUtils';

type HeroPlannerSchedulePostContract =
  CustomerCommunicationPaths['/api/customer-communication/v1/todays-escapes-schedule/hero-planner/schedules']['post'];

function mapHeroPlannerSchedule(
  external: HeroPlannerSchedulePostContract['responses']['200']['content']['application/json']['result'],
): CustomerCommunication.HeroPlannerSchedule {
  return {
    id: external.scheduleId,
    sendDate: getDayJs(external.identifiers.sendDate),
    brandId: external.identifiers.brand,
    countryGroupId: external.identifiers.countryGroup,
    countryId: external.identifiers.country,
    stateId: external.identifiers.state,
    cityId: external.identifiers.city,
    cadenceId: external.identifiers.cadence,
    membershipId: external.identifiers.loyalty,

    segmentId: external.identifiers.segment,
    emailSubjectLine: external.data.emailSubjectLine,
    emailPreHeader: external.data.emailPreheader,
    emailStatus: external.data.emailStatus,
    emailBannerId: external.data.emailBannerId,
    emailBannerPosition2Id: external.data.emailBannerPosition2Id,
    emailHeroOfferIds: external.data.emailHeroDeals,
    isApprovedForTpfm: external.data.isApprovedForTpfm,
    pushTitle: external.data.pushTitle,
    pushSubtitle: external.data.pushSubtitle,
    pushMessage: external.data.pushMessage,
    pushLink: external.data.pushLink,
    pushStatus: external.data.pushStatus,
    pushSvcImageId: external.data.pushImageId,
  };
}

function mapNewExternalHeroPlannerSchedule(
  internal: CustomerCommunication.UnsavedHeroPlannerSchedule,
): HeroPlannerSchedulePostContract['requestBody']['content']['application/json']['schedule'] {
  // map pushSchedules
  const pushSchedules = internal.pushSchedules?.map((pushSchedule) => {
    return {
      title: pushSchedule.title,
      subtitle: pushSchedule.subtitle,
      message: pushSchedule.message,
      link: pushSchedule.link,
      imageId: pushSchedule.imageId,
      scheduleType: pushSchedule.scheduleType,
      status: 'PENDING' as const,
    };
  });
  type Schedule = HeroPlannerSchedulePostContract['requestBody']['content']['application/json']['schedule'];
  return {
    identifiers: {
      brand: internal.brandId as Schedule['identifiers']['brand'],
      country: internal.countryId as Schedule['identifiers']['country'],
      countryGroup: internal.countryGroupId as Schedule['identifiers']['countryGroup'],
      state: internal.stateId as Schedule['identifiers']['state'],
      segment: internal.segmentId as Schedule['identifiers']['segment'],
      city: internal.cityId as Schedule['identifiers']['city'],
      cadence: internal.cadenceId as Schedule['identifiers']['cadence'],
      loyalty: internal.membershipId as Schedule['identifiers']['loyalty'],

      sendDate: internal.sendDate.format('YYYY-MM-DD'),
    },
    data: {
      emailSubjectLine: internal.emailSubjectLine,
      emailPreheader: internal.emailPreHeader,
      emailBannerId: internal.emailBannerId?.length >= 1 ? internal.emailBannerId : undefined,
      emailBannerPosition2Id:
        internal.emailBannerPosition2Id?.length >= 1 ? internal.emailBannerPosition2Id : undefined,
      // create doesn't accept null; there is no saved banner to remove yet
      banner: internal.banner ?? undefined,
      emailHeroDeals: internal.emailHeroOfferIds,
      emailStatus: 'PENDING',
      isApprovedForTpfm: internal.isApprovedForTpfm,

      pushOfferId: internal.emailHeroOfferIds[0],
      pushTitle: internal.pushTitle,
      pushSubtitle: internal.pushSubtitle,
      pushMessage: internal.pushMessage,
      pushLink: internal.pushLink,
      pushImageId: internal.pushSvcImageId,
      pushStatus: 'PENDING',
      pushSchedules,
    },
  };
}

async function requestPostHeroPlannerSchedule(unsavedSchedule: CustomerCommunication.UnsavedHeroPlannerSchedule) {
  if (!unsavedSchedule) throw 'missing unsavedSchedule';

  const url = makeCustomerCommunicationV1URL('/todays-escapes-schedule/hero-planner/schedules');

  return request(url.toString(), {
    method: 'POST',
    credentials: 'include',
    headers: json_headers,
    body: JSON.stringify({ schedule: mapNewExternalHeroPlannerSchedule(unsavedSchedule) }),
  })
    .then((response: HeroPlannerSchedulePostContract['responses']['200']['content']['application/json']) => {
      return mapHeroPlannerSchedule(response.result);
    })
    .catch((error) => {
      throw JSON.stringify(error, null, 2);
    });
}

export default requestPostHeroPlannerSchedule;
